import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Card, Badge, Button, Spinner, PageSkeleton } from '@daycal/ui';
import { api } from '../../lib/apiClient.js';
import { BOOKING_BASE_URL } from '../../lib/config.js';

// Global appointments log (architecture.md §24 M10). Platform-wide view of every booking
// across all hosts, with a manual "send mail" action (confirmation / reminder / cancellation).
const STATUS_TONE = {
  confirmed: 'success', completed: 'brand', cancelled: 'danger',
  hold: 'warning', awaiting_payment: 'warning', pending: 'neutral',
};

const TEMPLATES = [
  { value: 'booking_confirmed', label: 'Confirmation' },
  { value: 'booking_reminder', label: 'Reminder' },
  { value: 'booking_cancelled', label: 'Cancellation' },
];

function fmtWhen(startUtc, endUtc) {
  const d = new Date(startUtc);
  const date = d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const t = (x) => new Date(x).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  return { date, time: `${t(startUtc)} – ${t(endUtc)}` };
}

function BookingRow({ b }) {
  const [template, setTemplate] = useState('booking_confirmed');
  const [msg, setMsg] = useState('');
  const send = useMutation({
    mutationFn: () => api.adminSendBookingEmail(b.id, template),
    onSuccess: () => setMsg('Queued ✓'),
    onError: (err) => setMsg(err.message),
  });
  const when = fmtWhen(b.start_utc, b.end_utc);

  return (
    <tr className="transition-colors hover:bg-slate-50/50">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-slate-900">{b.invitee_name || '—'}</div>
        <div className="text-xs text-slate-500">{b.invitee_email || '—'}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-slate-900">{b.host_name || b.host_username}</div>
        <div className="text-xs text-slate-400">/u/{b.host_username}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <a 
          href={`${BOOKING_BASE_URL}/u/${b.host_username}`} 
          target="_blank" 
          rel="noreferrer" 
          className="text-indigo-600 hover:underline font-medium text-sm flex items-center gap-1"
        >
          View Booking Page
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
        </a>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-slate-700">{when.date}</div>
        <div className="text-xs text-slate-400">{when.time}</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <Badge tone={STATUS_TONE[b.status] || 'neutral'}>{b.status}</Badge>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="flex items-center justify-end gap-2 relative">
          <select
            value={template}
            onChange={(e) => { setTemplate(e.target.value); setMsg(''); }}
            className="rounded-lg border border-slate-400 px-2 py-1.5 text-xs outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 bg-white"
          >
            {TEMPLATES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Button onClick={() => { setMsg(''); send.mutate(); }} disabled={send.isPending} className="!py-1.5 !px-3 !text-xs bg-black hover:bg-slate-900 text-white border-none">
            {send.isPending ? <Spinner /> : 'Send mail'}
          </Button>
          {msg && <span className="absolute -bottom-5 right-0 text-[10px] font-medium text-slate-500">{msg}</span>}
        </div>
      </td>
    </tr>
  );
}

export default function AdminBookings() {
  const { data, isLoading } = useQuery({ queryKey: ['admin-bookings'], queryFn: api.adminListAllBookings });
  if (isLoading) return <PageSkeleton />;
  const bookings = data?.bookings || [];

  return (
    <div className="animate-dc-rise">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Global Appointments</h1>
          <p className="mt-1 text-sm text-slate-500">Platform-wide overview of all appointments across every host.</p>
        </div>
        <Badge tone="neutral">{bookings.length} total</Badge>
      </div>

      {bookings.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-slate-400 bg-slate-50 p-10 text-center">
          <h3 className="text-sm font-bold text-slate-900">No appointments yet</h3>
          <p className="mt-1 text-sm text-slate-500">Bookings across all hosts will appear here.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-slate-100 bg-white shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
          <table className="w-full text-left text-sm text-slate-500">
            <thead className="border-b border-slate-100 bg-slate-50/50 text-xs font-bold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-6 py-4">Invitee</th>
                <th className="px-6 py-4">Host</th>
                <th className="px-6 py-4">Booking Link</th>
                <th className="px-6 py-4">Date &amp; Time</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {bookings.map((b) => <BookingRow key={b.id} b={b} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
